import { Box, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { Tooltip } from "../../../ui/tooltip";
import { useState } from "react";
import useToaster from "../../../../hooks/useToaster";
import { ApiResponse } from "../../../../services/Dtos";
import ColorPill from "../../../CustomUi/ColorPill";
import { colors as colorOptions } from "../../../../services/constants";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import useThemeColors from "../../../../hooks/useThemeColors";
import CustomButton from "../../../CustomUi/CustomButton";

interface Props {
  color: string;
  handleSave: (media: string) => Promise<ApiResponse | null>;
  handleReset: () => Promise<ApiResponse | null>;
  successMessage: string;
  errorMessage: string;
  heading: string;
  body: string;
  buttonText: string;
}

export default function ColorSetting({
  color,
  handleSave,
  heading,
  body,
  buttonText,
}: Props) {
  const [tempColor, setTempColor] = useState<string>(color || "");
  const colors = useThemeColors();
  const [inColorLoading, setInColorLoading] = useState<boolean>(false);
  const { showSuccessToast, showErrorToast } = useToaster();
  const { accentColor } = useSchoolsContext();

  const onSave = async () => {
    setInColorLoading(true);
    const response = await handleSave(tempColor || "brand");

    if (response?.succeeded) {
      showSuccessToast(response.message || "");
    } else {
      showErrorToast(response?.message || "");
    }
    setInColorLoading(false);
  };

  return (
    <Box
      bg={colors.background.subtle}
      p={4}
      borderRadius="lg"
      borderWidth={1}
      borderColor={colors.border.primary}
    >
      <VStack gap={4} align="stretch">
        {/* Header Section */}
        <Flex justifyContent="space-between" alignItems="center">
          <VStack align="start" gap={1}>
            <Heading size="sm" color={colors.text.primary}>
              {heading}
            </Heading>
            <Text fontSize="sm" color={colors.text.primary} opacity={0.8}>
              {body}
            </Text>
          </VStack>
        </Flex>

        {/* Color Selection Grid */}
        <Box>
          <Flex gap={2} wrap="wrap" maxW="100%">
            {colorOptions.map((c) => (
              <Tooltip
                key={c.name}
                content={`Select ${c.name}`}
                showArrow
                positioning={{ placement: "bottom" }}
              >
                <Box>
                  <ColorPill
                    color={c}
                    selectedColor={tempColor}
                    setSelectedColor={setTempColor}
                  />
                </Box>
              </Tooltip>
            ))}
          </Flex>
        </Box>

        {/* Action Buttons */}
        <Flex gap={3} flexDir={["column"]} align={["stretch", "flex-start"]}>
          <CustomButton
            colorPalette={accentColor}
            variant="outline"
            label={buttonText || "Save Color"}
            onClick={onSave}
            loading={inColorLoading}
          />
        </Flex>
      </VStack>
    </Box>
  );
}
